import { Permission } from '../permissions/permissions.enum';
import type { ActingUserDto } from '../../../data-transfer-objects/src/lib/user.dto';

/**
 * True if `user.iam.platformPermissions` includes the given permission name (verify snapshot).
 * Missing `iam` or empty map returns false.
 *
 * @param user - Acting user from verify (or equivalent)
 * @param permission - Permission enum member or `CAN_*` string
 * @returns Whether IAM grants the permission on platform scope only
 */
export function hasIamPermission(
  user: ActingUserDto | null | undefined,
  permission: Permission | string
): boolean {
  const map = user?.iam?.platformPermissions;
  if (!map) {
    return false;
  }
  const key = permission as string;
  return map[key] === true;
}

/**
 * True if the acting user has the permission on the global (platform) scope after `/user/verify`:
 * union of legacy role and IAM platform grants. Prefers `user.permissions` (merged map); falls back
 * to `user.iam.platformPermissions` for partial DTOs or Storybook fixtures.
 *
 * @param user - Acting user from verify (or equivalent shape)
 * @param permission - Permission enum member or `CAN_*` string
 * @returns True when merged verify permissions or IAM platform snapshot grants the capability
 */
export function hasActingUserPlatformPermission(
  user: ActingUserDto | null | undefined,
  permission: Permission | string
): boolean {
  if (!user) {
    return false;
  }
  const key = permission as string;
  const merged = user.permissions;
  if (merged && Object.prototype.hasOwnProperty.call(merged, key)) {
    return merged[key] === true;
  }
  return user.iam?.platformPermissions?.[key] === true;
}
